import Konva from 'konva';
import {sumBy} from 'lodash';
import {KonvaEventObject} from 'konva/types/Node';
import {Line as KonvaLine, Rect} from 'react-konva';
import {KonvaPointerEvent} from 'konva/types/PointerEvents';
import React, {useCallback, useEffect, useState, useRef} from 'react';

import DeleteIconTag from './Tag';
import {BOX_LABEL_DEFAULT, BOX_LABEL_SELECTED, genId} from './const';
import {LabelPropsEvent, LineLabelProps, LinePropsEvent, Point} from './types';

type LineProps = {
  mousePosition: Point;
  isActiveTool: boolean;

  width?: number;
  height?: number;
  selected?: boolean;
  complete?: boolean;
  data?: LineLabelProps;
  closeTargetRadius?: number;
};

export function Line({
  width,
  height,
  isActiveTool,
  mousePosition,
  selected = false,
  complete = false,
  data = {id: '', points: []},

  onClick = () => {},
  onDelete = () => {},
  onComplete = () => {},
  onContextMenu = () => {},
}: LineProps & LabelPropsEvent & LinePropsEvent) {
  const rectRef = useRef<Konva.Rect>(null);
  const [points, setPoints] = useState<Point[]>([]);
  const [isComplete, setIsComplete] = useState(complete);
  const [nextPoint, setNextPoint] = useState<Point>({x: 0, y: 0});
  const [tagCenter, setTagCenter] = useState<Point>({x: 0, y: 0});

  const [isHover, setIsHover] = useState(false);
  const [labelStyle, setLabelStyle] = useState(BOX_LABEL_DEFAULT);

  const onPointDraw = useCallback(
    ({x, y}: Point) => {
      const newPoints = points.concat({x, y});

      setPoints(newPoints);

      if (newPoints.length === 2) {
        onComplete({...data, id: genId('LINE'), points: newPoints, complete: true});
        setNextPoint(points[0]);
        setIsComplete(true);
      }
    },
    [points, data, onComplete]
  );

  useEffect(() => {
    if (data) {
      const pArr = data.points;

      setPoints(pArr);

      // set tag location
      const centerX = sumBy(pArr, val => val.x) / pArr.length;
      const centerY = sumBy(pArr, val => val.y) / pArr.length;

      setTagCenter({x: centerX, y: centerY});
    }
  }, [data]);

  useEffect(() => {
    setLabelStyle(isHover || selected ? BOX_LABEL_SELECTED : BOX_LABEL_DEFAULT);
  }, [isHover, selected]);

  const onMouse = useCallback((isHovering: boolean) => setIsHover(isHovering), []);

  const onLocalContextMenu = useCallback(
    (event: KonvaPointerEvent) => {
      event.evt.preventDefault();

      if (onContextMenu) {
        onContextMenu(event);
      }
    },
    [onContextMenu]
  );

  const onLineClick = useCallback(
    (event: KonvaEventObject<MouseEvent>) => {
      event.evt.preventDefault();

      onClick(event);
    },
    [onClick]
  );

  const isCursorPointInsideLine = useCallback(
    (event: KonvaEventObject<MouseEvent>) => {
      const cursorPointX = event.evt.offsetX;
      const cursorPointY = event.evt.offsetY;
      const offset = 10;

      const x1 = points[0].x;
      const y1 = points[0].y;
      const x2 = points[1].x;
      const y2 = points[1].y;

      const distToLine =
        Math.abs(
          (y2 - y1) * cursorPointX - (x2 - x1) * cursorPointY + x2 * y1 - y2 * x1
        ) / Math.sqrt((y2 - y1) ** 2 + (x2 - x1) ** 2);

      return distToLine <= offset;
    },
    [points]
  );

  useEffect(() => {
    if (isActiveTool && !isComplete) {
      setNextPoint(mousePosition);
    }
  }, [isActiveTool, isComplete, mousePosition]);

  return (
    <>
      <KonvaLine
        {...labelStyle}
        lineJoin="round"
        closed={isComplete}
        hitStrokeWidth={20}
        shadowForStrokeEnabled
        points={points
          .flatMap(point => [point.x, point.y])
          .concat(isComplete ? [] : [nextPoint.x, nextPoint.y])}
        onClick={onLineClick}
        onMouseEnter={event => {
          onMouse(isCursorPointInsideLine(event));
        }}
        onMouseLeave={event => {
          onMouse(isCursorPointInsideLine(event));
        }}
        onContextMenu={onLocalContextMenu}
      />

      {/* Hit Area */}
      {!isComplete && (
        <Rect
          x={0}
          y={0}
          ref={rectRef}
          width={width}
          height={height}
          onClick={event => {
            const x = event.evt.offsetX;
            const y = event.evt.offsetY;

            onPointDraw({x, y});
          }}
        />
      )}
      {isComplete && (
        <DeleteIconTag
          id={data.id}
          label={data.label}
          {...tagCenter}
          selected={selected || isHover}
          onDelete={onDelete}
          yBuffer={40}
          onClick={onLineClick}
          onContextMenu={onLocalContextMenu}
        />
      )}
    </>
  );
}
